"use client";

import { ReactNode } from "react";
import { Button, Icon } from "@focal/ui-kit";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

interface DetailPageHeaderProps {
  /** If provided, a back Button is rendered that navigates to this route. */
  backHref?: string;
  /** Page title — typically an H5 or an editable title component. */
  title: ReactNode;
  /** Optional meta row rendered below the title (date, tags, status, etc.). */
  meta?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** Optional extra class names appended to the outer wrapper. */
  className?: string;
}

export const DetailPageHeader = ({
  backHref,
  title,
  meta,
  actions,
  className,
}: DetailPageHeaderProps) => {
  const router = useRouter();

  // Outer wrapper declares the container so the inner `<header>` and the
  // actions div can query its width. CSS container queries can't observe the
  // container's own size, so the responsive flex utilities must live on a
  // descendant, not the container element itself.
  return (
    <div className="@container/header">
      <header
        className={cn(
          "flex flex-col gap-3",
          "@[640px]/header:flex-row @[640px]/header:items-start @[640px]/header:justify-between @[640px]/header:gap-4",
          "pl-16 pr-6 lg:pl-6 py-4 border-b border-border-light",
          className,
        )}
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {backHref ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.push(backHref)}
              aria-label="Go back"
              data-cy="page-back"
            >
              <Icon name="ChevronLeftIcon" />
            </Button>
          ) : null}
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="min-w-0">{title}</div>
            {meta ? <div className="flex items-center gap-2">{meta}</div> : null}
          </div>
        </div>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 @[640px]/header:shrink-0">
            {actions}
          </div>
        ) : null}
      </header>
    </div>
  );
};
